import React, { useEffect, useRef, useState } from 'react';
import { Player } from '../types';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';

interface PlayerActionsModalProps {
  isVisible: boolean;
  player: Player | null;
  onClose: () => void;

  // Routing helpers
  onViewProfile?: () => void;
  onCreateReview?: () => void;
  onViewReviews?: () => void;
  onEditPlayer?: () => void;
  onRemovePlayer?: () => void;
  onSelfRemove?: () => void;
  onMove?: () => void;
  onConfirmPayment?: () => Promise<unknown> | void;

  // State flags
  canCreateReview?: boolean;
  hasReviewedPlayer?: boolean;
  canViewReviews?: boolean;
  loadingReviews?: boolean;
  canEdit?: boolean;
  canRemove?: boolean;
  canSelfRemove?: boolean;
  canMove?: boolean;
  canConfirmPayment?: boolean;
  isOwnPlayer?: boolean;
}

interface ActionItem {
  key: string;
  icon: string;
  label: string;
  subtitle?: string;
  color: string;
  onClick: () => void;
  disabled?: boolean;
}

export const PlayerActionsModal: React.FC<PlayerActionsModalProps> = ({
  isVisible,
  player,
  onClose,
  onViewProfile,
  onCreateReview,
  onViewReviews,
  onEditPlayer,
  onRemovePlayer,
  onSelfRemove,
  canCreateReview = false,
  hasReviewedPlayer = false,
  canViewReviews = false,
  loadingReviews = false,
  canEdit = false,
  canRemove = false,
  canSelfRemove = false,
  canMove = false,
  onMove,
  canConfirmPayment = false,
  onConfirmPayment,
  isOwnPlayer = false,
}) => {
  const { isMobile } = useResponsive();
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (!isVisible || !player) return null;

  const initials = player.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const actions: ActionItem[] = [];

  // Ver perfil completo (no es own player y tiene userId)
  if (!isOwnPlayer && player.user && onViewProfile) {
    actions.push({
      key: 'view-profile',
      icon: '👤',
      label: 'Ver perfil',
      subtitle: 'Información del jugador',
      color: '#8B5CF6',
      onClick: () => { onViewProfile(); onClose(); },
    });
  }

  // Dejar review
  if (canCreateReview && onCreateReview) {
    const disabled = hasReviewedPlayer || loadingReviews;
    actions.push({
      key: 'create-review',
      icon: loadingReviews ? '⏳' : '⭐',
      label: loadingReviews ? 'Cargando...' : hasReviewedPlayer ? 'Ya le hiciste una reseña' : 'Dejar reseña',
      subtitle: hasReviewedPlayer ? 'Reseña enviada' : 'Calificar al jugador',
      color: '#FF6B35',
      disabled,
      onClick: () => {
        if (!disabled) {
          onCreateReview();
          onClose();
        }
      },
    });
  }

  // Ver reviews
  if (onViewReviews) {
    actions.push({
      key: 'view-reviews',
      icon: '📝',
      label: canViewReviews ? 'Ver reseñas' : 'Deja una reseña',
      subtitle: canViewReviews ? 'Calificaciones recibidas' : 'Deja tu reseña y mira qué te pusieron',
      color: canViewReviews ? theme.colors.primary : theme.colors.textSecondary,
      disabled: !canViewReviews,
      onClick: () => {
        if (canViewReviews) {
          onViewReviews();
          onClose();
        }
      },
    });
  }

  // Confirmar pago (admin, solo si todavía no está confirmado)
  if (canConfirmPayment && onConfirmPayment) {
    actions.push({
      key: 'confirm-payment',
      icon: confirmingPayment ? '⏳' : '💰',
      label: confirmingPayment ? 'Confirmando...' : 'Confirmar pago',
      subtitle: 'Marca el pago de este jugador como recibido',
      color: '#4CAF50',
      disabled: confirmingPayment,
      onClick: async () => {
        if (confirmingPayment) return;
        setConfirmingPayment(true);
        try {
          await onConfirmPayment();
        } finally {
          if (mountedRef.current) {
            setConfirmingPayment(false);
            onClose();
          }
        }
      },
    });
  }

  // Cambiar de equipo (admin)
  if (canMove && onMove) {
    actions.push({
      key: 'move',
      icon: '🔄',
      label: 'Cambiar de equipo',
      subtitle: 'Mover o intercambiar con otro jugador',
      color: '#FB923C',
      onClick: () => { onMove(); onClose(); },
    });
  }

  // Editar (own o admin)
  if (canEdit && onEditPlayer) {
    actions.push({
      key: 'edit',
      icon: '✏️',
      label: isOwnPlayer ? 'Editar mi nombre' : 'Editar jugador',
      subtitle: isOwnPlayer ? 'Actualizar perfil' : 'Posición y valoración',
      color: '#4CAF50',
      onClick: () => { onEditPlayer(); onClose(); },
    });
  }

  // Eliminar (admin sobre otro)
  if (canRemove && onRemovePlayer) {
    actions.push({
      key: 'remove',
      icon: '🗑',
      label: 'Eliminar del partido',
      subtitle: 'Acción irreversible',
      color: theme.colors.danger,
      onClick: () => { onRemovePlayer(); onClose(); },
    });
  }

  // Darme de baja (own player no-creator)
  if (canSelfRemove && onSelfRemove) {
    actions.push({
      key: 'self-remove',
      icon: '↩️',
      label: 'Darme de baja',
      subtitle: 'Salir del partido',
      color: theme.colors.danger,
      onClick: () => { onSelfRemove(); onClose(); },
    });
  }

  return (
    <div
      data-testid="player-actions-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? 0 : '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: isMobile ? '20px 20px 0 0' : theme.borderRadius.card,
          padding: theme.spacing.lg,
          maxWidth: isMobile ? '100%' : '460px',
          width: '100%',
          maxHeight: isMobile ? '90vh' : '85vh',
          overflowY: 'auto',
          boxShadow: theme.colors.shadow,
          border: `1px solid ${theme.colors.border}`,
        }}
      >
        {/* Handle bar */}
        {isMobile && (
          <div style={{
            width: '40px',
            height: '4px',
            backgroundColor: theme.colors.border,
            borderRadius: '2px',
            margin: '0 auto 16px',
          }} />
        )}

        {/* Close button (desktop) */}
        {!isMobile && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '22px',
                cursor: 'pointer',
                color: theme.colors.textSecondary,
                padding: '4px 8px',
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Player header */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '20px',
        }}>
          {player.user?.avatar ? (
            <img
              src={player.user.avatar}
              alt={player.name}
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2px solid ${theme.colors.primary}`,
              }}
            />
          ) : (
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              backgroundColor: theme.colors.primary,
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              fontWeight: 700,
              fontFamily: theme.fonts.heading,
            }}>
              {initials}
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              color: theme.colors.text,
              fontSize: '18px',
              fontWeight: 700,
              fontFamily: theme.fonts.heading,
            }}>
              {player.name}
            </div>
            {player.position && (
              <div style={{
                color: theme.colors.textSecondary,
                fontSize: '13px',
                marginTop: '2px',
              }}>
                {player.position}
              </div>
            )}
            {typeof player.rating === 'number' && (
              <div style={{ marginTop: '4px', fontSize: '14px' }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} style={{
                    color: i < Math.floor(player.rating) ? theme.colors.warning : theme.colors.border,
                  }}>★</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions list */}
        {actions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: theme.colors.textSecondary,
            padding: theme.spacing.lg,
            fontFamily: theme.fonts.body,
            fontSize: theme.fonts.sizeSmall,
          }}>
            No hay acciones disponibles para este jugador.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {actions.map((action) => (
              <button
                key={action.key}
                data-testid={`player-action-${action.key}`}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  backgroundColor: theme.colors.background,
                  border: `1px solid ${action.disabled ? theme.colors.border : action.color}`,
                  borderRadius: theme.borderRadius.button,
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  opacity: action.disabled ? 0.5 : 1,
                  transition: 'background-color 0.15s ease',
                  fontFamily: theme.fonts.body,
                }}
                onMouseEnter={(e) => {
                  if (!action.disabled) {
                    e.currentTarget.style.backgroundColor = action.color + '15';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.background;
                }}
              >
                <span style={{ fontSize: '22px', flexShrink: 0 }}>{action.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: action.disabled ? theme.colors.textSecondary : theme.colors.text,
                    fontWeight: 600,
                    fontSize: '15px',
                  }}>
                    {action.label}
                  </div>
                  {action.subtitle && (
                    <div style={{
                      color: theme.colors.textSecondary,
                      fontSize: '12px',
                      marginTop: '2px',
                    }}>
                      {action.subtitle}
                    </div>
                  )}
                </div>
                {!action.disabled && (
                  <span style={{ color: theme.colors.textSecondary, fontSize: '16px' }}>›</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerActionsModal;
